/* eslint-disable @typescript-eslint/no-explicit-any */
import { Box, Chip, Stack, Tooltip, Typography, useTheme } from '@mui/material';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { colors, TextFieldComponent } from '../../components';
import CustomButton from '../../components/button/ButtonComponent';
import { CustomDrawer } from '../../components/drawer/CustomDrawer';
import { CustomSelect } from '../../components/select/CustomSelect';
import CommonIconAvatar from '../../components/ui-components/CommonIconAvatar';
import { createSupportTicket, SupportRequestType } from '../../services/support-service';
import { openToast } from '../../store/reducers/toastSlice';
import {
    trackSupportRequestStarted,
    trackSupportTicketCreated,
    trackSupportTicketCreationFailed,
} from '../../utils/google-analytics';
import { ALLOWED_FILE_EXTENSIONS } from '../../utils/helper';
import { useTranslation } from '../../utils/translations';
// import { CustomRadio } from '../../components/radio';

interface CreateSupportRequestModalProps {
    open: boolean;
    onClose: () => void;
    onSubmit: (data: any) => void;
    contracts?: Array<{ id: string; name: string }>;
    requestTypes?: SupportRequestType[];
    selectedRequestType?: any;
}

export const CreateSupportRequestModal: React.FC<CreateSupportRequestModalProps> = ({
    open,
    onClose,
    onSubmit,
    contracts = [],
    requestTypes = [],
    selectedRequestType,
}) => {
    const theme = useTheme();
    const { t } = useTranslation();
    const dispatch = useDispatch();

    // Helper function to get translation with fallback
    const getTranslation = (key: string, fallback: string): string => {
        const translated = t(key);
        return translated !== key ? translated : fallback;
    };
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [requestType, setRequestType] = useState('');
    const [contractNumber, setContractNumber] = useState('');
    const [subject, setSubject] = useState('');
    const [contactMethod, setContactMethod] = useState<'email' | 'phone'>('email');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [details, setDetails] = useState('');
    const [otherRequestType, setOtherRequestType] = useState('');
    const [loading, setLoading] = useState(false);
    const [uploadedFile, setUploadedFile] = useState<{ name: string; file: File } | null>(null);
    const [errors, setErrors] = useState<{
        requestType?: string;
        contractNumber?: string;
        details?: string;
        subject?: string;
        otherRequestType?: string;
    }>({});

    // Shared toast dispatcher to avoid repeating the openToast boilerplate everywhere
    const showToast = useCallback(
        (message: string, severity: 'error' | 'success' = 'error') => {
            dispatch(
                openToast({
                    show: true,
                    isAlertOpen: true,
                    message,
                    severity,
                }),
            );
        },
        [dispatch],
    );

    // Backend sometimes returns a generic "extension not allowed" message; reformat it consistently
    const formatBackendErrorMessage = (message: string): string => {
        if (message.includes('not Allowed') || message.includes('Please enable this extension')) {
            const extensionMatch = message.match(/\.([a-z0-9]+)/i);
            const extension = extensionMatch ? extensionMatch[1] : 'this file type';
            return `Unsupported File Type. ${extension} Files Are Not Allowed.`;
        }
        return message;
    };

    // Clears the native file input's value so the same file can be re-selected later
    const resetFileInput = () => {
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const validateForm = (): boolean => {
        const newErrors: {
            requestType?: string;
            details?: string;
            subject?: string;
            otherRequestType?: string;
        } = {};

        if (!requestType) {
            newErrors.requestType = t('support.modal.validation.requestTypeRequired') || 'Request type is required';
        }

        if (!subject || subject.trim() === '') {
            newErrors.subject = t('support.modal.validation.subjectRequired') || 'Subject is required';
        }

        // if (!contractNumber || contractNumber.length === 0) {
        //     newErrors.contractNumber =
        //         t('support.modal.validation.contractNumberRequired') || 'Contract ID is required';
        // }

        if (!details || details.trim() === '') {
            newErrors.details = t('support.modal.validation.detailsRequired') || 'Description is required';
        } else if (details.length > 2000) {
            newErrors.details = 'Details cannot exceed 2000 characters';
        }

        // Validate otherRequestType if "Others" is selected
        const requestTypeName = getRequestTypeName(requestType);
        if (
            (requestTypeName.toLowerCase() === 'others' || requestTypeName.toLowerCase() === 'other') &&
            (!otherRequestType || otherRequestType.trim() === '')
        ) {
            newErrors.otherRequestType = getTranslation(
                'support.modal.validation.otherRequestTypeRequired',
                'Please specify the request type',
            );
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Helper function to get request type name/label from value (which could be a number ID or string)
    const getRequestTypeName = useCallback(
        (value: any): string => {
            if (!value) return '';

            // If value is already a string, return it
            if (typeof value === 'string' && isNaN(Number(value))) {
                return value;
            }

            // If value is a number (or numeric string), find the corresponding request type in requestTypes array
            if (typeof value === 'number' || (typeof value === 'string' && !isNaN(Number(value)))) {
                const numericValue = typeof value === 'number' ? String(value) : value;
                const foundType = requestTypes.find(type => String(type.id) === String(numericValue));
                return foundType ? foundType.name : '';
            }

            // Fallback: try to get name or label from value object
            return (value as any)?.name || (value as any)?.label || '';
        },
        [requestTypes],
    );

    const isSupportFormReady = useMemo(() => {
        if (!requestType) return false;
        if (!subject.trim()) return false;
        if (!details.trim()) return false;
        if (details.length > 2000) return false;
        const requestTypeName = getRequestTypeName(requestType);
        if (
            (requestTypeName.toLowerCase() === 'others' || requestTypeName.toLowerCase() === 'other') &&
            !otherRequestType.trim()
        ) {
            return false;
        }
        return true;
    }, [requestType, subject, details, otherRequestType, getRequestTypeName]);

    const handleRequestTypeChange = (value: any) => {
        setRequestType(value);
        // Get the request type name/label from the value
        const requestTypeName = getRequestTypeName(value);

        // Check if the request type is "Others" or "Other"
        if (requestTypeName.toLowerCase() !== 'others' && requestTypeName.toLowerCase() !== 'other') {
            setOtherRequestType('');
            if (errors.otherRequestType) {
                setErrors(prev => ({ ...prev, otherRequestType: undefined }));
            }
        }
        if (errors.requestType) {
            setErrors(prev => ({ ...prev, requestType: undefined }));
        }
    };

    const handleOtherRequestTypeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setOtherRequestType(e.target.value);
        if (errors.otherRequestType) {
            setErrors(prev => ({ ...prev, otherRequestType: undefined }));
        }
    };

    const handleContractNumberChange = (value: string) => {
        setContractNumber(value);
        if (errors.contractNumber) {
            setErrors(prev => ({ ...prev, contractNumber: undefined }));
        }
    };

    const handleSubjectChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSubject(e.target.value);
        if (errors.subject) {
            setErrors(prev => ({ ...prev, subject: undefined }));
        }
    };

    // Show whitespace-only error immediately when user leaves the Subject field.
    const handleSubjectBlur = () => {
        if (subject.length > 0 && subject.trim() === '') {
            setErrors(prev => ({
                ...prev,
                subject: t('support.modal.validation.subjectRequired') || 'Subject is required',
            }));
        }
    };

    const handleDetailsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        // Limit to 2000 characters
        if (value.length <= 2000) {
            setDetails(value);
            if (errors.details) {
                setErrors(prev => ({ ...prev, details: undefined }));
            }
        } else {
            // If exceeds limit, show error but don't update the value
            setErrors(prev => ({
                ...prev,
                details: 'Details cannot exceed 2000 characters',
            }));
        }
    };

    // Show whitespace-only error immediately when user leaves the Details field.
    const handleDetailsBlur = () => {
        if (details.length > 0 && details.trim() === '') {
            setErrors(prev => ({
                ...prev,
                details: t('support.modal.validation.detailsRequired') || 'Description is required',
            }));
        }
    };

    const resetForm = useCallback(() => {
        setRequestType('');
        setContractNumber('');
        setSubject('');
        setContactMethod('email');
        setEmail('');
        setPhone('');
        setDetails('');
        setOtherRequestType('');
        setUploadedFile(null);
        setErrors({});
        // Reset file input
        resetFileInput();
    }, []);

    // Reset form when modal opens
    useEffect(() => {
        if (open) {
            resetForm();
            trackSupportRequestStarted('support_page');
        }
    }, [open, resetForm]);

    // Set request type when selectedRequestType prop is provided and modal is open
    useEffect(() => {
        if (open && selectedRequestType !== undefined && selectedRequestType !== null && selectedRequestType !== '') {
            // Delay setting to avoid showing the index number during drawer opening animation and loader
            const timer = setTimeout(() => {
                setRequestType(selectedRequestType);
            }, 300);
            return () => clearTimeout(timer);
        }
    }, [selectedRequestType, open]);

    const handleSubmit = async () => {
        if (!validateForm()) {
            return;
        }

        try {
            setLoading(true);
            // Prepare payload according to API format
            // Extract requestType ID if it's an object, otherwise use the value directly
            const requestTypeId =
                typeof requestType === 'object' && requestType !== null
                    ? (requestType as any).id || (requestType as any).Id || requestType
                    : requestType;

            // Check if "Others" is selected and include otherRequestType
            const requestTypeName = getRequestTypeName(requestType);
            const isOthersSelected =
                requestTypeName.toLowerCase() === 'others' || requestTypeName.toLowerCase() === 'other';

            const payload: any = {
                subject: subject.trim(),
                description: details.trim(),
                contractId: contractNumber,
                requestType: requestTypeId,
            };

            // Add otherRequestType if "Others" is selected

            if (isOthersSelected && otherRequestType !== '') {
                payload.otherReason = otherRequestType.trim();
            }

            // Add file if uploaded - validate file size before adding
            if (uploadedFile) {
                // Double-check: prevent 0 KB files from being submitted
                if (uploadedFile.file.size === 0) {
                    showToast('Empty Files (0 KB) Are Not Allowed. Please Upload a Valid File.');
                    setUploadedFile(null);
                    setLoading(false);
                    return;
                }
                payload.file = uploadedFile.file;
            }
            const response = await createSupportTicket(payload, true);

            if (response.statusCode === 200 || response.statusCode === 201) {
                // Track successful support ticket creation
                trackSupportTicketCreated(requestTypeName, contractNumber, subject);

                showToast(response.message || 'Support Request Created Successfully.', 'success');

                // Reset form after successful submission
                resetForm();

                // Close the modal first
                onClose();

                // Call the onSubmit callback to refresh support list
                const formData = {
                    requestType,
                    contractNumber,
                    subject,
                    contactMethod,
                    email: contactMethod === 'email' ? email : '',
                    phone: contactMethod === 'phone' ? phone : '',
                    details,
                };
                onSubmit(formData);
            } else {
                // Handle backend error messages and reformat them
                const errorMessage = formatBackendErrorMessage(
                    response.message || 'Unable to create the support request.',
                );

                trackSupportTicketCreationFailed(errorMessage);
                showToast(errorMessage);
            }
        } catch (error: any) {
            console.error('Error creating support request:', error);

            // Handle error responses and reformat backend messages
            const errorMessage = formatBackendErrorMessage(
                error?.response?.data?.message || error?.message || 'Error Creating Support Request. Please Try Again.',
            );

            trackSupportTicketCreationFailed(errorMessage);
            showToast(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        // Reset form and errors when closing
        resetForm();
        onClose();
    };

    const handleUploadClick = () => {
        fileInputRef.current?.click();
    };

    // Maximum file size: 10MB
    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB in bytes

    const validateFileExtension = (fileName: string): boolean => {
        const extension = fileName.split('.').pop()?.toLowerCase();
        return extension ? ALLOWED_FILE_EXTENSIONS.includes(extension) : false;
    };

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) {
            return;
        }

        // Validate file extension
        if (!validateFileExtension(file.name)) {
            const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'unknown';
            showToast(`Unsupported File Type. ${fileExtension} Files Are Not Allowed.`);
            resetFileInput();
            return;
        }

        // Validate file size - must be greater than 0 KB
        if (file.size === 0) {
            showToast('Empty Files (0 KB) Are Not Allowed. Please Upload a Valid File.');
            // Reset file input and clear uploaded file state
            resetFileInput();
            setUploadedFile(null);
            return;
        }

        // Validate file size - must not exceed maximum size
        if (file.size > MAX_FILE_SIZE) {
            const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
            showToast(`File Size Exceeds the ${fileSizeMB} MB Limit. Please Upload a Smaller File.`);
            // Reset file input and clear uploaded file state
            resetFileInput();
            setUploadedFile(null);
            return;
        }

        // Store the file (single file only)
        setUploadedFile({ name: file.name, file });

        // Reset file input
        resetFileInput();
    };

    const handleRemoveFile = () => {
        setUploadedFile(null);
        // Reset file input
        resetFileInput();
    };

    return (
        <CustomDrawer
            open={open}
            onClose={handleClose}
            anchor="right"
            width={500}
            title={t('support.modal.title')}
            sticky={true}
            footer={
                <>
                    <Box
                        sx={{
                            pt: 3,
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'end',
                        }}
                    >
                        <CustomButton
                            variant="outlined"
                            onClick={handleClose}
                            disabled={loading}
                            sx={{
                                width: 'fit-content',
                            }}
                        >
                            {t('support.modal.buttons.cancel')}
                        </CustomButton>
                        <CustomButton
                            variant="contained"
                            onClick={handleSubmit}
                            disabled={loading || !isSupportFormReady}
                            sx={{ width: 'fit-content' }}
                        >
                            {loading ? 'Submitting...' : t('support.modal.buttons.submit')}
                        </CustomButton>
                    </Box>
                </>
            }
        >
            <Box>
                <Stack spacing={2}>
                    {/* Request Type */}
                    <CustomSelect
                        title={t('support.modal.requestType.label')}
                        ariaLabel={t('support.modal.requestType.label')}
                        labelSrOnly
                        placeholder={t('support.modal.requestType.placeholder')}
                        value={requestType || ''}
                        onChange={handleRequestTypeChange}
                        options={requestTypes}
                        required
                        errorValue={errors.requestType}
                        maxHeight={400}
                    />
                    {/* Additional field for "Others" request type */}
                    {(() => {
                        const requestTypeName = getRequestTypeName(requestType);
                        const isOthersSelected =
                            requestTypeName.toLowerCase() === 'others' || requestTypeName.toLowerCase() === 'other';

                        return isOthersSelected ? (
                            <TextFieldComponent
                                label={getTranslation('support.modal.otherRequestType.label', 'Describe request type')}
                                required
                                value={otherRequestType}
                                onChange={handleOtherRequestTypeChange}
                                variant="outlined"
                                errorValue={errors.otherRequestType}
                            />
                        ) : null;
                    })()}
                    {/* Contract ID */}
                    <CustomSelect
                        title={t('support.modal.contractID.label')}
                        ariaLabel={t('support.modal.contractID.label')}
                        labelSrOnly
                        placeholder={t('support.modal.contractID.placeholder')}
                        value={contractNumber}
                        onChange={handleContractNumberChange}
                        options={contracts}
                        errorValue={errors.contractNumber}
                    />
                    {/* Subject */}
                    <TextFieldComponent
                        label={t('support.modal.subject.label')}
                        required
                        value={subject}
                        onChange={handleSubjectChange}
                        onBlur={handleSubjectBlur}
                        variant="outlined"
                        errorValue={errors.subject}
                        maxLength={255}
                    />
                    {/* the below code for feature reference */}
                    {/* <Box>
                            <Typography variant="subtitle2" sx={{ mb: 1, color: colors.primaryLight[100] }}>
                                {t('support.modal.contactMethod.label')}
                            </Typography>
                            <RadioGroup
                                value={contactMethod}
                                onChange={e => setContactMethod(e.target.value as 'email' | 'phone')}
                                sx={{
                                    '&.MuiFormGroup-root': {
                                        gap: 2,
                                        '& .MuiFormControlLabel-label': {
                                            color: colors.primaryLight[100],
                                            fontSize: 16,
                                        },
                                        '& .MuiButtonBase-root': {
                                            '& svg': {
                                                height: 18,
                                                width: 18,
                                            }
                                        }
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        border: `1px solid ${contactMethod === 'email' ? colors.warningPreset[30] : colors.greyPreset[30]}`,
                                        bgcolor: contactMethod === 'email' ? colors.bluePreset[110] : colors.fullWhite,
                                        borderRadius: 2,
                                        p: 2,
                                    }}
                                >
                                    <FormControlLabel
                                        value="email"
                                        control={<CustomRadio />}
                                        label={t('support.modal.contactMethod.email')}
                                        sx={{
                                            mb: contactMethod === 'email' ? 1.5 : 0,
                                        }}
                                    />
                                    {contactMethod === 'email' && (
                                        <Box>
                                            <TextFieldComponent
                                                label={t('support.modal.contactMethod.emailPlaceholder')}
                                                value={email}
                                                onChange={e => setEmail(e.target.value)}
                                                variant="outlined"
                                                type="email"
                                            />
                                        </Box>
                                    )}
                                </Box>

                                <Box
                                    sx={{
                                        border: `1px solid ${contactMethod === 'phone' ? colors.warningPreset[30] : colors.greyPreset[30]}`,
                                        borderRadius: 2,
                                        p: 2,
                                        backgroundColor: contactMethod === 'phone' ? colors.bluePreset[110] : colors.fullWhite,
                                    }}
                                >
                                    <FormControlLabel
                                        value="phone"
                                        control={<CustomRadio />}
                                        label={t('support.modal.contactMethod.phone')}
                                        sx={{
                                            mb: contactMethod === 'phone' ? 1.5 : 0,
                                        }}
                                    />
                                    {contactMethod === 'phone' && (
                                        <Box>
                                            <TextFieldComponent
                                                label={t('support.modal.contactMethod.phonePlaceholder')}
                                                value={phone}
                                                onChange={e => setPhone(e.target.value)}
                                                variant="outlined"
                                                type="tel"
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </RadioGroup>
                        </Box> */}
                    <Box>
                        <TextFieldComponent
                            label={t('support.modal.details.label')}
                            required
                            value={details}
                            onChange={handleDetailsChange}
                            onBlur={handleDetailsBlur}
                            errorValue={errors.details}
                            multiline
                            rows={5}
                            maxLength={2000}
                        />
                        <Typography
                            variant="caption"
                            sx={{
                                mt: 0.5,
                                color: details.length > 2000 ? colors.errorPreset[10] : colors.greyPreset[80],
                                textAlign: 'right',
                            }}
                        >
                            {details.length}/2000 characters
                        </Typography>
                    </Box>
                    {/* Upload Document Section */}
                    <Box>
                        {/* <Typography variant="subtitle2" sx={{ mb: 1, color: colors.primaryLight[100] }}>
                                {t('support.modal.uploadDocument.label')}
                            </Typography> */}
                        <input
                            ref={fileInputRef}
                            type="file"
                            aria-label={t('support.modal.uploadDocument.button') || 'Upload document'}
                            style={{ display: 'none' }}
                            accept=".jpg,.jpeg,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.xml,.png"
                            onChange={handleFileChange}
                        />
                        {!uploadedFile ? (
                            <Stack
                                direction={'row'}
                                gap={1}
                                alignItems={'center'}
                                onClick={handleUploadClick}
                                sx={{ cursor: 'pointer', mb: 1 }}
                            >
                                <span
                                    className="icon-attachment"
                                    style={{ fontSize: 20, fontWeight: 500, color: colors.primaryLight[100] }}
                                />
                                <Typography variant="body1" sx={{ color: theme.palette.text.primary }}>
                                    {t('support.modal.uploadDocument.button')}
                                </Typography>
                            </Stack>
                        ) : (
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: colors.primaryLight[5],
                                    border: `1px solid ${colors.greyPreset[30]}`,
                                    gap: 1.5,
                                }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0 }}>
                                    <CommonIconAvatar icon="icon-documents" iconSize={16} />
                                    <Tooltip title={uploadedFile.name} placement="top">
                                        <Typography variant="body2" color="textPrimary" noWrap sx={{ maxWidth: 220 }}>
                                            {uploadedFile.name}
                                        </Typography>
                                    </Tooltip>
                                </Box>
                                <Chip
                                    label={t('support.modal.uploadDocument.remove')}
                                    size="small"
                                    onClick={handleRemoveFile}
                                    sx={{
                                        cursor: 'pointer',
                                        bgcolor: colors.fullWhite,
                                        color: colors.primaryLight[100],
                                        border: `1px solid ${colors.greyPreset[30]}`,
                                        '&:hover': {
                                            bgcolor: colors.greyPreset[10],
                                        },
                                    }}
                                />
                            </Box>
                        )}
                    </Box>
                </Stack>
            </Box>
        </CustomDrawer>
    );
};
